/**
 * Data layer for `auto-action-block-mark-reject-alert-on-fraud`. Thin typed fetch wrappers over
 * /api/v1/auto-action-block-mark-reject-alert-on-fraud. Raw `fetch()` is confined to this file
 * per the dash contract.
 *
 * Backend maps a fraud risk signal to an automated response action (block/mark/reject/alert/clean)
 * and persists the non-"clean" decisions. This module exposes the list feed, per-event lookup,
 * rollup summary, and the create (decide+record) mutation.
 */

export type FraudAction = "block" | "mark" | "reject" | "alert" | "clean";
export type FraudSubjectType = "click" | "conversion" | "ip" | "user";
export type FraudSeverity = "low" | "med" | "high" | "critical";

export interface FraudActionRow {
	id: number;
	tenant_id: string;
	event_id: string;
	subject_type: FraudSubjectType;
	subject_id: string;
	action: Exclude<FraudAction, "clean">;
	risk_score: number;
	reason: string;
	reasons: string[];
	severity: FraudSeverity;
	status: string;
	created_at: number;
}

export interface ListFraudActionsResponse {
	items: FraudActionRow[];
}

export interface FraudActionSummaryBucket {
	action: Exclude<FraudAction, "clean">;
	count: number;
}

export interface FraudActionSummaryResponse {
	by_action: FraudActionSummaryBucket[];
	total: number;
}

export interface CreateFraudActionBody {
	event_id: string;
	subject_type: FraudSubjectType;
	subject_id: string;
	risk_score?: number;
	action?: FraudAction;
	severity?: FraudSeverity;
	reason?: string;
	reasons?: string[];
}

export interface CreateFraudActionResponse {
	ok: true;
	skipped: boolean;
	id?: number;
	tenant_id: string;
	event_id: string;
	subject_type: FraudSubjectType;
	subject_id: string;
	action: FraudAction;
	risk_score: number;
	reason?: string;
	reasons?: string[];
	severity: FraudSeverity;
	status?: string;
	created_at?: number;
}

const BASE = "/api/v1/auto-action-block-mark-reject-alert-on-fraud";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export interface ListFraudActionsParams {
	action?: Exclude<FraudAction, "clean">;
	subject_type?: FraudSubjectType;
}

export async function list(
	params: ListFraudActionsParams = {},
): Promise<ListFraudActionsResponse> {
	const qs = new URLSearchParams();
	if (params.action) qs.set("action", params.action);
	if (params.subject_type) qs.set("subject_type", params.subject_type);
	const suffix = qs.toString() ? `?${qs.toString()}` : "";
	const res = await fetch(`${BASE}${suffix}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListFraudActionsResponse>(res);
}

export async function summary(): Promise<FraudActionSummaryResponse> {
	const res = await fetch(`${BASE}/summary`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<FraudActionSummaryResponse>(res);
}

export async function create(
	body: CreateFraudActionBody,
): Promise<CreateFraudActionResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateFraudActionResponse>(res);
}

export const autoActionBlockMarkRejectAlertOnFraudQueryKeys = {
	all: ["auto-action-block-mark-reject-alert-on-fraud"] as const,
	list: (params: ListFraudActionsParams = {}) =>
		[
			...autoActionBlockMarkRejectAlertOnFraudQueryKeys.all,
			"list",
			params,
		] as const,
	summary: () =>
		[...autoActionBlockMarkRejectAlertOnFraudQueryKeys.all, "summary"] as const,
	detail: (id: string) =>
		[...autoActionBlockMarkRejectAlertOnFraudQueryKeys.all, id] as const,
};
